/**
 * Painel flutuante da emissão de NFS-e: fica sempre visível (mesmo com o modal fechado ou rolado) enquanto a
 * emissão roda e, se falhar, permanece na tela com o máximo de detalhes técnicos até o usuário fechar.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';
import {
  CheckCircle,
  Close,
  ContentCopy,
  ErrorOutline,
  ExpandLess,
  ExpandMore,
  RadioButtonUnchecked
} from '@mui/icons-material';

export type EmissionMessage = { codigo?: string; mensagem?: string; correcao?: string };

export type EmissionStatus = {
  phase: 'running' | 'success' | 'pending' | 'rejected' | 'no-response';
  invoiceId: string;
  provider: 'ipm' | 'nacional';
  startedAt: number;
  finishedAt?: number | undefined;
  testMode?: boolean | undefined;
  providerAccepted?: boolean | undefined;
  persisted?: boolean | undefined;
  numero?: string | undefined;
  reason?: string | undefined;
  messages: EmissionMessage[];
  /** HTTP do vover-admin → billing. */
  httpStatus?: number | undefined;
  /** Corpo completo devolvido pelo billing (stages.diagnostics, stages.error, stages.context...). */
  body?: unknown | undefined;
};

type StepState = 'wait' | 'ok' | 'fail' | 'skip';

const rec = (v: unknown): Record<string, unknown> =>
  v && typeof v === 'object' ? (v as Record<string, unknown>) : {};
const str = (v: unknown): string => (v == null ? '' : String(v));

const StepIcon: React.FC<{ s: StepState }> = ({ s }) =>
  s === 'wait' ? (
    <CircularProgress size={18} />
  ) : s === 'ok' ? (
    <CheckCircle color="success" fontSize="small" />
  ) : s === 'fail' ? (
    <ErrorOutline color="error" fontSize="small" />
  ) : (
    <RadioButtonUnchecked color="disabled" fontSize="small" />
  );

const Pre: React.FC<{ children: React.ReactNode; maxHeight?: number }> = ({
  children,
  maxHeight = 180
}) => (
  <Box
    component="pre"
    sx={{
      m: 0,
      p: 1,
      bgcolor: 'grey.100',
      borderRadius: 1,
      fontSize: 11,
      maxHeight,
      overflow: 'auto',
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word'
    }}
  >
    {children}
  </Box>
);

const Field: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) =>
  value === undefined || value === null || value === '' ? null : (
    <Typography variant="caption" display="block" sx={{ wordBreak: 'break-word' }}>
      <strong>{label}:</strong> {value}
    </Typography>
  );

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Box sx={{ mt: 1.5 }}>
    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
      {title}
    </Typography>
    {children}
  </Box>
);

function pretty(raw: string): string {
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}

const NfseEmissionFloatingStatus: React.FC<{
  status: EmissionStatus | null;
  onClose: () => void;
}> = ({ status, onClose }) => {
  const [expanded, setExpanded] = useState(true);
  const [now, setNow] = useState(() => Date.now());
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (status?.phase !== 'running') return undefined;
    const t = window.setInterval(() => setNow(Date.now()), 500);
    return () => window.clearInterval(t);
  }, [status?.phase]);

  // Nova emissão: reabre expandido. Sucesso some sozinho; erro/pendência fica até fechar.
  useEffect(() => {
    if (status?.phase === 'running') setExpanded(true);
  }, [status?.phase, status?.startedAt]);
  useEffect(() => {
    if (status?.phase !== 'success') return undefined;
    const t = window.setTimeout(onClose, 20000);
    return () => window.clearTimeout(t);
  }, [status?.phase, status?.finishedAt, onClose]);

  const body = useMemo(() => rec(status?.body), [status?.body]);
  const stages = useMemo(() => rec(body.stages), [body]);
  const diag = useMemo(() => rec(stages.diagnostics), [stages]);
  const err = useMemo(() => rec(stages.error), [stages]);
  const ctx = useMemo(() => rec(stages.context), [stages]);

  if (!status) return null;

  const running = status.phase === 'running';
  const provName = status.provider === 'nacional' ? 'Sistema Nacional' : 'IPM';
  const elapsed = Math.max(0, Math.round(((status.finishedAt ?? now) - status.startedAt) / 1000));

  const step1: StepState = running
    ? 'wait'
    : status.providerAccepted
      ? 'ok'
      : status.phase === 'no-response'
        ? 'skip'
        : 'fail';
  const step2: StepState = running
    ? 'wait'
    : status.phase === 'success'
      ? 'ok'
      : status.phase === 'no-response'
        ? 'skip'
        : 'fail';

  const failed = status.phase === 'rejected' || status.phase === 'no-response';
  const severity =
    status.phase === 'success'
      ? 'success'
      : failed
        ? 'error'
        : status.phase === 'pending'
          ? 'warning'
          : 'info';

  const dpsXml = str(diag.dpsXml);
  const raw = str(diag.rawResponse);
  const errors = Array.isArray(diag.erros) ? (diag.erros as Array<Record<string, unknown>>) : [];
  const alertas = Array.isArray(diag.alertas)
    ? (diag.alertas as Array<Record<string, unknown>>)
    : [];

  const copyAll = async () => {
    const payload = {
      resumo: {
        fase: status.phase,
        provedor: status.provider,
        modoTeste: status.testMode,
        motivo: status.reason,
        duracaoSegundos: elapsed,
        httpAdminBilling: status.httpStatus
      },
      mensagens: status.messages,
      resposta: status.body
    };
    try {
      await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2500);
    } catch {
      setCopied(false);
    }
  };

  const title = running
    ? `Emitindo NFS-e — ${provName}`
    : status.phase === 'success'
      ? `NFS-e emitida${status.numero ? ` (nº ${status.numero})` : ''}`
      : status.phase === 'pending'
        ? 'NFS-e em processamento'
        : status.phase === 'no-response'
          ? 'Sem resposta durante a emissão'
          : `Emissão recusada — ${provName}`;

  return (
    <Paper
      elevation={10}
      role="status"
      aria-live="polite"
      sx={{
        position: 'fixed',
        right: 16,
        bottom: 16,
        zIndex: 1450,
        width: 'min(560px, calc(100vw - 32px))',
        maxHeight: '80vh',
        display: 'flex',
        flexDirection: 'column',
        border: theme =>
          `2px solid ${
            severity === 'error'
              ? theme.palette.error.main
              : severity === 'success'
                ? theme.palette.success.main
                : severity === 'warning'
                  ? theme.palette.warning.main
                  : theme.palette.info.main
          }`
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ p: 1.25, pb: 0.75 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={700} noWrap>
            {title}
          </Typography>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.25 }}>
            <Chip size="small" label={`${elapsed}s`} variant="outlined" />
            {status.testMode !== undefined && (
              <Chip
                size="small"
                variant="outlined"
                color={status.testMode ? 'info' : 'warning'}
                label={status.testMode ? 'Homologação (sem valor fiscal)' : 'Produção (NFS-e real)'}
              />
            )}
          </Stack>
        </Box>
        <Tooltip title={copied ? 'Copiado!' : 'Copiar detalhes (JSON)'}>
          <span>
            <IconButton size="small" onClick={() => void copyAll()} disabled={running}>
              <ContentCopy fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <IconButton
          size="small"
          onClick={() => setExpanded(e => !e)}
          aria-label="Recolher/expandir"
        >
          {expanded ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
        <Tooltip title={running ? 'Disponível ao concluir' : 'Fechar'}>
          <span>
            <IconButton size="small" onClick={onClose} disabled={running} aria-label="Fechar">
              <Close fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      <Collapse in={expanded} unmountOnExit={false}>
        <Box sx={{ px: 1.5, pb: 1.5, overflowY: 'auto', maxHeight: 'calc(80vh - 70px)' }}>
          <Stack spacing={1}>
            <Stack direction="row" spacing={1} alignItems="center">
              <StepIcon s={step1} />
              <Typography variant="body2">
                {running
                  ? `Enviando ao ${provName} e aguardando o retorno (pode levar até 2 minutos)…`
                  : step1 === 'ok'
                    ? status.provider === 'nacional'
                      ? status.testMode
                        ? 'DPS aceita pelo Sistema Nacional em produção restrita (sem valor fiscal)'
                        : 'NFS-e autorizada pelo Sistema Nacional'
                      : status.testMode
                        ? 'XML validado pelo IPM (modo teste — nada emitido na prefeitura)'
                        : 'NFS-e aceita pelo IPM (emitida na prefeitura)'
                    : step1 === 'skip'
                      ? `Retorno do ${provName} desconhecido (sem resposta do billing)`
                      : status.provider === 'nacional'
                        ? 'O Sistema Nacional não aceitou a DPS'
                        : 'O IPM não aceitou o XML'}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <StepIcon s={step2} />
              <Typography variant="body2">
                {running
                  ? 'Salvar NFS-e no banco'
                  : step2 === 'ok'
                    ? `NFS-e persistida no banco${status.numero ? ` (nº ${status.numero})` : ''}`
                    : step2 === 'skip'
                      ? 'Persistência não confirmada'
                      : 'Nenhuma NFS-e gerada; a tentativa foi registrada como rejeitada'}
              </Typography>
            </Stack>
          </Stack>

          {running && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              Não feche a página: este painel acompanha a emissão até o fim e mostra o resultado
              completo.
            </Typography>
          )}

          {!running && failed && (
            <Alert severity="error" sx={{ mt: 1.5 }}>
              <Typography variant="body2" fontWeight={600}>
                {status.reason || 'A emissão não foi concluída.'}
              </Typography>
              {status.phase === 'no-response' && (
                <Typography variant="caption" display="block">
                  A emissão pode ter sido concluída; confira o status da NFS-e antes de tentar de
                  novo (a tentativa seguinte consulta a DPS antes de reenviar).
                </Typography>
              )}
            </Alert>
          )}

          {!running && status.messages.length > 0 && (
            <Section title={`Mensagens do ${provName}`}>
              <Box component="ul" sx={{ m: 0, pl: 2.5, typography: 'body2' }}>
                {status.messages.map((m, i) => (
                  <Box component="li" key={`${m.codigo}-${i}`} sx={{ mb: 0.75 }}>
                    <strong>{m.codigo ? `[${m.codigo}] ` : ''}</strong>
                    {m.mensagem}
                    {m.correcao && (
                      <Box sx={{ color: 'text.secondary' }}>Como corrigir: {m.correcao}</Box>
                    )}
                  </Box>
                ))}
              </Box>
            </Section>
          )}

          {!running &&
            (alertas.length > 0 || errors.length > 0) &&
            status.messages.length === 0 && (
              <Section title="Erros / alertas do provedor">
                <Pre>{JSON.stringify({ erros: errors, alertas }, null, 2)}</Pre>
              </Section>
            )}

          {!running && (Object.keys(diag).length > 0 || status.httpStatus !== undefined) && (
            <Section title="Diagnóstico da chamada">
              <Field label="Provedor" value={str(diag.provider) || status.provider} />
              <Field label="Ambiente" value={str(diag.ambiente)} />
              <Field label="HTTP do provedor" value={str(diag.httpStatus)} />
              <Field label="Endpoint" value={str(diag.endpoint)} />
              <Field label="Id da DPS" value={str(diag.idDps)} />
              <Field
                label="nDPS / série"
                value={diag.nDps ? `${str(diag.nDps)} / ${str(diag.serie)}` : ''}
              />
              <Field label="NFS-e recuperada por consulta" value={diag.recovered ? 'sim' : ''} />
              <Field label="HTTP admin → billing" value={str(status.httpStatus)} />
            </Section>
          )}

          {!running && Object.keys(err).length > 0 && (
            <Section title="Erro interno do billing">
              <Field label="Tipo" value={str(err.name)} />
              <Field label="Mensagem" value={str(err.message)} />
              <Field label="Código" value={str(err.code)} />
              <Field label="HTTP" value={str(err.httpStatus)} />
              {Array.isArray(err.missing) && (err.missing as string[]).length > 0 && (
                <Field
                  label="Pendências de configuração"
                  value={(err.missing as string[]).join('; ')}
                />
              )}
              {str(err.responseBody) && (
                <>
                  <Typography variant="caption" display="block">
                    <strong>Resposta:</strong>
                  </Typography>
                  <Pre>{pretty(str(err.responseBody))}</Pre>
                </>
              )}
              {str(err.stack) && (
                <>
                  <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                    <strong>Stack (resumo):</strong>
                  </Typography>
                  <Pre maxHeight={120}>{str(err.stack)}</Pre>
                </>
              )}
            </Section>
          )}

          {!running && raw && (
            <Section title="Resposta bruta do provedor">
              <Pre>{pretty(raw)}</Pre>
            </Section>
          )}

          {!running && dpsXml && (
            <Section title="DPS enviada (sem a assinatura)">
              <Pre maxHeight={220}>{dpsXml}</Pre>
            </Section>
          )}

          {!running && Object.keys(ctx).length > 0 && (
            <Section title="Contexto">
              <Field label="Fatura" value={str(ctx.invoiceId)} />
              <Field label="Tenant" value={str(ctx.tenantId)} />
              <Field label="NFS-e (billing)" value={str(ctx.nfseExternalId)} />
              <Field label="Status registrado" value={str(ctx.nfseStatus)} />
              <Field label="Ambiente do billing" value={str(ctx.nodeEnv)} />
              <Field label="Início" value={str(ctx.startedAt)} />
              <Field label="Fim" value={str(ctx.finishedAt)} />
            </Section>
          )}

          {!running && failed && (
            <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
              <Button
                size="small"
                variant="outlined"
                startIcon={<ContentCopy />}
                onClick={() => void copyAll()}
              >
                {copied ? 'Copiado!' : 'Copiar todos os detalhes'}
              </Button>
              <Button size="small" onClick={onClose}>
                Fechar
              </Button>
            </Stack>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
};

export default NfseEmissionFloatingStatus;
